import { useRef } from 'react'
import type { ChartData, ChartOptions, ChartType as ChartJSType } from 'chart.js'
import { ImageDown } from 'lucide-react'
import { CustomChart } from '@/components/charts/CustomChart'
import { Button } from '@/components/ui/button'
import type { ChatMessagePart } from '../types'
import { downloadChartAsImage } from '../export'
import { ShareButton } from './actionBar'

type CustomChartPart = Extract<ChatMessagePart, { kind: 'custom_chart' }>

interface Props {
  part: CustomChartPart
  onShare?: () => void
}

/**
 * Catch-all renderer for charts the model produces via `render_chart_custom`.
 * The model passes a Chart.js config blob (type + data + options); we hand
 * it to the generic CustomChart wrapper, which fills in theme defaults so
 * the chart still respects light/dark mode and palette.
 */
export function CustomChartBlock({ part, onShare }: Props) {
  const { title, config } = part
  const wrapperRef = useRef<HTMLDivElement | null>(null)
  const chartType = (config?.type as ChartJSType) || 'bar'
  // The config passes through untyped so the model can emit arbitrary
  // Chart.js variants; we just hand it to Chart.js which does its own
  // validation at runtime.
  const data = (config?.data ?? { datasets: [] }) as unknown as ChartData<ChartJSType>
  const options = (config?.options ?? {}) as unknown as ChartOptions<ChartJSType>
  const exportTitle = title || `${chartType} chart`

  return (
    <div className="my-3 overflow-hidden rounded-lg border border-border bg-card p-3">
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        {title ? (
          <div className="min-w-0 truncate text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            {title}
          </div>
        ) : <span />}
        <div className="flex shrink-0 items-center gap-1">
          <Button
            type="button"
            variant="outline"
            size="icon"
            onClick={() => downloadChartAsImage(wrapperRef.current, exportTitle, 'image/jpeg')}
            className="size-7"
            title="Download as image"
            aria-label="Download as image"
          >
            <ImageDown className="size-3.5" />
          </Button>
          <ShareButton onClick={onShare} />
        </div>
      </div>
      <div ref={wrapperRef}>
        <CustomChart chartType={chartType} data={data} options={options} height={320} />
      </div>
    </div>
  )
}
